import { graphql } from 'react-relay';
import type { GraphQLError } from 'graphql';

import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { expValEquals } from '@confluence/feature-experiments';

import WebItemLocationQuery, {
	type WebItemLocationQuery$data,
	type WebItemLocationQuery$variables,
} from './__generated__/WebItemLocationQuery.graphql.ts';
import type {
	preloadWebItemLocationWithSpaceKeyQuery$data,
	preloadWebItemLocationWithSpaceKeyQuery$variables,
} from './__generated__/preloadWebItemLocationWithSpaceKeyQuery.graphql.ts';
import { SYSTEM_HEADER_LEFT, SYSTEM_USER } from './webFragmentLocations.ts';

const preloadWebItemLocationWithSpaceKeyQuery = graphql`
	query preloadWebItemLocationWithSpaceKeyQuery(
		$location: String
		$locations: [String]
		$spaceKey: String
		$version: Int
	) {
		space(key: $spaceKey) {
			id
			homepageId
			homepageWebSections(location: $location, locations: $locations, version: $version) {
				id
				label
				cacheKey
				styleClass
				items {
					accessKey
					completeKey
					hasCondition
					id
					icon {
						height
						width
						path
					}
					params {
						key
						value
					}
					label
					moduleKey
					section
					styleClass
					tooltip
					url
					urlWithoutContextPath
					weight
				}
			}
		}
	}
`;

const isSystemBatchLocation = (location: string[]) =>
	location.length === 2 && location.includes(SYSTEM_HEADER_LEFT) && location.includes(SYSTEM_USER);

const filterSectionsByLocations = (
	webItemSections: NonNullable<WebItemLocationQuery$data['webItemSections']>,
	locations: string[],
) =>
	webItemSections.filter((section) =>
		locations.some((location) => section?.id?.startsWith(`${location}/`)),
	);

const writeWebItemLocationCache = ({
	locations,
	contentId,
	spaceKey,
	version,
	webItemSections,
}: {
	locations: string[];
	contentId: string | null;
	spaceKey: string | null;
	version: number | null;
	webItemSections: NonNullable<WebItemLocationQuery$data['webItemSections']>;
}) => {
	writeQueryInterop<WebItemLocationQuery$data, WebItemLocationQuery$variables>({
		query: WebItemLocationQuery,
		variables: {
			location: null,
			locations,
			contentId,
			spaceKey,
			version,
		},
		data: { webItemSections },
	});
};

const writeWebItemLocationEntries = ({
	locations,
	contentId,
	spaceKey,
	version,
	webItemSections,
}: {
	locations: string[];
	contentId: string | null;
	spaceKey: string | null;
	version: number | null;
	webItemSections: NonNullable<WebItemLocationQuery$data['webItemSections']>;
}) => {
	locations.forEach((location) => {
		writeQueryInterop<WebItemLocationQuery$data, WebItemLocationQuery$variables>({
			query: WebItemLocationQuery,
			variables: {
				location,
				locations: [],
				contentId,
				spaceKey,
				version,
			},
			data: {
				webItemSections: filterSectionsByLocations(webItemSections, [location]),
			},
		});
	});
};

type PreloadWebItemLocationContext = {
	single?: boolean;
};

export function preloadWebItemLocation({
	location = null,
	contentId = null,
	spaceKey = null,
	version = null,
	context,
}: {
	location?: string | string[] | null;
	contentId?: string | null;
	spaceKey?: string | null;
	version?: number | null;
	context?: PreloadWebItemLocationContext;
} = {}): Promise<void | {
	data: WebItemLocationQuery$data | null;
	errors?: ReadonlyArray<GraphQLError>;
	hasErrors: boolean;
}> {
	if (!contentId && spaceKey) {
		return query<
			preloadWebItemLocationWithSpaceKeyQuery$data,
			preloadWebItemLocationWithSpaceKeyQuery$variables
		>({
			query: preloadWebItemLocationWithSpaceKeyQuery,
			variables: {
				location: Array.isArray(location) ? null : location,
				locations: Array.isArray(location) ? location : [],
				spaceKey,
				version,
			},
			context,
		}).then(({ data }) => {
			if (data?.space?.homepageId) {
				writeQueryInterop<WebItemLocationQuery$data, WebItemLocationQuery$variables>({
					query: WebItemLocationQuery,
					variables: {
						location: Array.isArray(location) ? null : location,
						locations: Array.isArray(location) ? location : [],
						contentId: data?.space?.homepageId,
						spaceKey: null,
						version,
					},
					data: {
						webItemSections: data?.space?.homepageWebSections || [],
					},
				});
			}
		});
	}
	const variables = {
		// GraphQL doesn't cache single location strings properly when passed to locations
		// unless we're using multiple locations we should be using the location variable
		location: Array.isArray(location) ? null : location,
		locations: Array.isArray(location) ? location : [],
		contentId,
		spaceKey,
		version,
	};

	const result = query<WebItemLocationQuery$data, WebItemLocationQuery$variables>({
		query: WebItemLocationQuery,
		variables,
		context,
	});

	if (Array.isArray(location)) {
		const shouldWriteSystemBatchEntries =
			expValEquals('web_item_query_batch_system_call', 'isEnabled', true) &&
			isSystemBatchLocation(location);

		if (shouldWriteSystemBatchEntries) {
			void result.then(({ data }) => {
				if (!data?.webItemSections) {
					return;
				}

				writeWebItemLocationCache({
					locations: location,
					contentId,
					spaceKey,
					version,
					webItemSections: data.webItemSections,
				});
				writeWebItemLocationEntries({
					locations: location,
					contentId,
					spaceKey,
					version,
					webItemSections: data.webItemSections,
				});
			});
		}
	}

	return result;
}
